import React from "react";
import { debounce } from "lodash";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";

import { AddErrorAlert } from "../../slices/alert";

import {
  fetchUsers,
  postUser,
  putUser,
  deleteUser,
  getUser,
  putUserMeta,
  lookupUsers,
} from "../../lib/api";
import { chunkArray } from "../../utilities";
import { fetchTypes } from "../../slices/userType";
import { selectAccess } from "../../slices/accessSlice";
import useAccess, { PERMS } from "../../hooks/useAccess";
import { useEnvironment } from "../EnvironmentProvider";
import { useAuth } from "../AuthProvider";
import { getRoles, selectRole } from "../../slices/role";
import {
  selectUser,
  updateLoaded,
  updateUser as updateUserAction,
  updateUsers as updateUsersAction,
  deleteUsers as deleteUsersAction,
  updateLoading,
  clearLoading,
  usersLoaded,
  pageLoad,
  resetPagination as resetPaginationAction,
  gotoPage as gotoPageAction,
  merge,
  formatUser,
} from "../../slices/user";
import useUserPermissions from "../../hooks/permissions/useUserPermissions";
import { loadUserActivities } from "@SonarCX/slices/activity";

type UserAPIType = {
  getUsers: (isExternal: boolean) => { [id: string]: LocalUser };
  uploadUser: (
    user: any,
    addChannels?: boolean,
    onSuccess?: (x: any) => void,
    typeObj?: any,
    onFail?: (x: any) => void,
  ) => Promise<void>;
  deleteUsers: (ids: string[]) => Promise<void>;
  updateUser: (user: any) => void;
  updateUsers: (users: APIUser[]) => void;
  loadUser: (id: string | string[]) => void;
  findChannels: (channels: string[]) => { name: string; channel: APIChannel }[];
  propagateMerge: (from: string, to: string) => Promise<void>;
  updateUserMeta: (
    id: string,
    vals: any,
    old: any,
    user: any,
  ) => Promise<{ key: string; value: string }[]>;
  getPageOfUsers: (
    external: boolean,
    limit: number,
    page: number,
  ) => Promise<{ [x: string]: LocalUser }>;
  resetPagination: (external: boolean) => void;
  gotoPage: (external: boolean, page: number) => void;
  allUsers: { [x: string]: LocalUser };
  loading: string[];
  loaded: boolean;
  pagination: { true: CachedPagination; false: CachedPagination };
  error: any;
  userNotFound: any;
  setUserNotFound: React.Dispatch<any>;
  handleEvent: (msg: any) => void;
};

const UserAPIContext = React.createContext<UserAPIType>(null);

export const DEFAULT_EMBEDS = ["auth", "tasks", "teams", "media"];
const MAX_BULK_CHUNK_SIZE = 25;
const DEBOUNCE_DELAY = 300;

const UserAPIProvider = ({ children }) => {
  const dispatch = useAppDispatch();
  const {
    types,
    loaded: userTypesLoaded,
    loading: userTypesLoading,
  } = useAppSelector((storeState) => storeState.userType);
  const { loaded: permissionsLoaded } = useAppSelector(selectAccess);
  const {
    loaded: rolesLoaded,
    loading: rolesLoading,
    adminRole,
  } = useAppSelector(selectRole);
  const {
    loaded: userLoaded,
    loading: userLoading,
    users,
    pagination,
  } = useAppSelector(selectUser);

  const { getEnvValue, user } = useEnvironment();
  const hasPermission = useAccess();
  const { isLoggedIn } = useAuth();
  const { hasFull: canGetRoles, hasBasic } = useUserPermissions();

  const [error, setError] = React.useState<any>("");
  const [userNotFound, setUserNotFound] = React.useState(null);
  const [agentsLoaded, setAgentsLoaded] = React.useState(false);
  const [eventQueue, setEventQueue] = React.useState([]);

  // Load roles when possible
  React.useEffect(() => {
    if (isLoggedIn && !rolesLoaded && !rolesLoading && permissionsLoaded) {
      dispatch(getRoles(canGetRoles));
    }
  }, [
    isLoggedIn,
    dispatch,
    canGetRoles,
    rolesLoaded,
    rolesLoading,
    permissionsLoaded,
  ]);

  React.useEffect(() => {
    if (isLoggedIn && !userTypesLoaded && !userTypesLoading) {
      dispatch(fetchTypes());
    }
  }, [isLoggedIn, userTypesLoaded, userTypesLoading, dispatch]);

  // Fake load of users
  React.useEffect(() => {
    if (rolesLoaded) {
      dispatch(updateLoaded());
    }
  }, [rolesLoaded, dispatch]);

  // Update user info locally
  const updateUser: UserAPIType["updateUser"] = React.useCallback(
    (user) => {
      const formatted = formatUser(user);
      dispatch(updateUserAction(formatted));
    },
    [dispatch],
  );

  // Update info for mutliple users at once
  const updateUsers: UserAPIType["updateUsers"] = React.useCallback(
    (users) => {
      dispatch(updateUsersAction(users));
    },
    [dispatch],
  );

  // Send user to api
  const uploadUser: UserAPIType["uploadUser"] = React.useCallback(
    async (
      user,
      addChannels = false,
      onSuccess = (_) => {},
      typeObj,
      onFail = (_) => {},
    ) => {
      if (!user) {
        return;
      }

      setError("");

      // Get needed values
      const newUser = {
        name: user.name ?? "",
        role_id: user.role_id !== "" ? user.role_id : null,
        avatar_id: user.avatar_id !== "" ? user.avatar_id : null,
        type_id:
          user.type_id ??
          getEnvValue("config.users", "u_default_external_type_id", null) ??
          getEnvValue("config.users", "users_default_external_type_id", null),
        status_id: user.status_id !== "" ? user.status_id : null,
        tags: user.tags ?? [],
        id: user.id,
        timezone: user.timezone !== "" ? user.timezone : null,
        media_ids: user.media_ids ?? [],
      };

      // modify newUser object to respect type id
      if (typeObj) {
        if (typeObj.name === "User") {
          // @ts-ignore
          if (!newUser.auth) {
            // @ts-ignore
            newUser.auth = null;
          }
        }
      }

      if (user.auth?.email?.length) {
        // @ts-ignore
        newUser.auth = {
          email: user.auth?.email,
          suspended_at: user.auth.suspended_at ?? null,
        };
      }

      // Update auth info
      if (user.role_id) {
        // @ts-ignore
        newUser.auth = {
          suspended_at: user.auth?.suspended_at ?? null,
          email: user.auth?.email ?? user?.email,
        };

        if (["Customer", "Guest"].includes(user.role_id)) {
          newUser.role_id = null;
        }
      }

      // removing empty auth object to prevent 422 empty auth
      if (
        // @ts-ignore
        (user.role_id === adminRole && !newUser.auth?.email?.length) ||
        // @ts-ignore
        newUser.auth === ""
      ) {
        // @ts-ignore
        newUser.auth = null;
      }

      // @ts-ignore
      // Get channels
      newUser.channels = (user.channels || []).map((channel) => {
        // API only accepts these
        return {
          id: channel.id,
          type: channel.type,
          name: channel.name,
          value: channel.value,
        };
      });

      // Only set channels for customers
      if (addChannels) {
        // @ts-ignore
        newUser.channels = [
          { type: "sms_voice", name: "Phone", value: user.phone },
          { type: "email", name: "Email", value: user?.email },
        ];
      }

      // Override auth to revoke access
      if (user.auth === "null") {
        // @ts-ignore
        newUser.auth = null;
      }

      const sendMethod = user.id ? putUser : postUser;

      try {
        // if no suspended at, assume inactive status
        // @ts-ignore
        if (newUser.auth) {
          // @ts-ignore
          if (!Object.keys(newUser.auth).includes("suspended_at")) {
            // @ts-ignore
            newUser.auth.suspended_at = new Date().toISOString();
          }
        }

        // @ts-ignore
        const res = await sendMethod(newUser);

        // @ts-ignore
        const uploaded = formatUser(res);
        updateUser(res);
        onSuccess(uploaded);
      } catch (err) {
        const res = err.response;
        console.error("Error response", res);
        onFail(err);

        if (res.status === 400) {
          setError(res.data.errorMessage);
          dispatch(AddErrorAlert({ err }));
          // } else if (res?.status === 403 && res.data === 'Session Expired') {
          // window.location.href = '/login'
        } else {
          dispatch(AddErrorAlert({ err }));
        }
      }
    },
    [updateUser, getEnvValue, adminRole, dispatch],
  );

  // Deletes a list of users
  const deleteUsers: UserAPIType["deleteUsers"] = React.useCallback(
    async (ids) => {
      const deleteSingleUser = async (id) => {
        try {
          await deleteUser(id);
        } catch (err) {
          console.error("Delete Error", err);
          dispatch(AddErrorAlert({ err }));
        }
      };

      await Promise.all(ids.map(deleteSingleUser));
      dispatch(deleteUsersAction(ids));
    },
    [dispatch],
  );

  // Filter users by external status
  const getUsers: UserAPIType["getUsers"] = React.useCallback(
    (isExternal) => {
      const filteredUsers = {};
      Object.entries(users).forEach(([id, user]) => {
        const match =
          String(types.find((type) => type.id === user.type_id)?.external) ===
          String(isExternal);
        if (match) {
          filteredUsers[id] = user;
        }
      });
      return filteredUsers;
    },
    [users, types],
  );

  // Queues a user for loading
  const loadUser: UserAPIType["loadUser"] = React.useCallback(
    (id) => {
      if (!id || !id.length) {
        return;
      }

      dispatch(updateLoading(Array.isArray(id) ? id : [id]));
    },
    [dispatch],
  );

  // Loads a set of users, applies chunking to calls
  const loadUsersAsync = React.useCallback(
    async (ids) => {
      const chunks = chunkArray(ids, MAX_BULK_CHUNK_SIZE);

      const userResults = await Promise.all(
        chunks.map(async (chunk) => {
          if (chunk.length) {
            setUserNotFound(null);
            try {
              return await lookupUsers(chunk, DEFAULT_EMBEDS);
            } catch (err) {
              setUserNotFound(true);
              console.error("Error looking up users", err);
            }
          }
          return [];
        }),
      ).catch((err) => dispatch(AddErrorAlert({ err })));

      if (!userResults) {
        return [];
      }

      return userResults.flat().map(formatUser);
    },
    [dispatch],
  );

  // Loads any queued users
  React.useEffect(() => {
    const loadable = userLoading.filter((id) => !(id in users)).filter(Boolean);

    if (!userLoaded || !loadable.length) {
      return;
    }

    const func = debounce(() => {
      dispatch(clearLoading());
      loadUsersAsync(loadable)
        .then((users) => {
          dispatch(usersLoaded(users));
        })
        .catch((err) => dispatch(AddErrorAlert({ err })));
    }, DEBOUNCE_DELAY);

    func();

    return func.cancel;
  }, [userLoading, userLoaded, users, loadUsersAsync, dispatch]);

  // Matches channel ids to users
  const findChannels: UserAPIType["findChannels"] = React.useCallback(
    (channels) => {
      const found = channels.map((id) => {
        let channel = null;

        const user = Object.values(users).find((user) => {
          channel = user.channels?.find((ch) => ch.id === id);
          return channel;
        });
        const name = user?.name;

        if (name && channel) {
          return { name, channel };
        }
        return null;
      });

      return found.filter(Boolean);
    },
    [users],
  );

  // update user meta
  const updateUserMeta: UserAPIType["updateUserMeta"] = React.useCallback(
    async (id, vals, old, user) => {
      const data = [];

      // editing existing values
      if (old) {
        const modified = { ...vals };

        // replace uploaded values with null if empty
        old.forEach((pair) => {
          const { key } = pair;
          if (modified[key] === "") {
            modified[key] = null;
          }
        });

        // loop through object to remove values === ''
        for (let key in modified) {
          if (modified[key] === "") {
            delete modified[key];
          }
        }

        // send tags to user as array
        if (modified.tags.length) {
          const newUser = {
            id,
            ...user,
            tags: modified.tags,
          };
          user.tags = modified.tags;
          try {
            const afterPut = await putUser(newUser);
            updateUser(afterPut);
          } catch (err) {
            console.error("Error updating user meta", err);
            dispatch(AddErrorAlert({ err }));
          }
        }

        delete modified.tags;

        // change to key-value pairs for api
        Object.entries(modified).forEach(([key, value]) => {
          data.push({ key, value: value ? value.toString() : null });
        });
      } else {
        // adding new values to newly created user
        const clean = Object.entries(vals).reduce((obj, [key, value]) => {
          if (value) {
            obj[key] = value;
          }
          return obj;
        }, {});

        // @ts-ignore
        if (!clean.tags.length) {
          // @ts-ignore
          delete clean.tags;
        }

        // change to key-value pairs for api
        Object.entries(clean).forEach(([key, value]) => {
          data.push({ key, value: value.toString() });
        });
      }

      try {
        const meta = await putUserMeta(id, data);
        return meta;
      } catch (err) {
        console.error("Error updating user meta");
        setError({ err });
        return null;
      }
    },
    [updateUser, dispatch],
  );

  // Get a list of users of the selected type
  const getPageOfUsers: UserAPIType["getPageOfUsers"] = React.useCallback(
    async (external, limit, page) => {
      try {
        const res = await fetchUsers(DEFAULT_EMBEDS, external, limit, page);

        return new Promise((resolve, reject) => {
          dispatch(pageLoad({ res, external, cb: resolve, page }));
        });
      } catch (err) {
        console.error("Error getting users", err);
      }
    },
    [dispatch],
  );

  // Resets pagination for one of the lists
  const resetPagination: UserAPIType["resetPagination"] = React.useCallback(
    (external) => {
      dispatch(resetPaginationAction(external));
    },
    [dispatch],
  );

  // Goes to a specific page of users
  const gotoPage: UserAPIType["gotoPage"] = React.useCallback(
    (external, page) => {
      dispatch(gotoPageAction({ external, page }));
    },
    [dispatch],
  );

  // Seed the system with some agents
  // More agents are loaded via pagination/search
  React.useEffect(() => {
    if (!agentsLoaded && userLoaded && permissionsLoaded) {
      if (!hasBasic) {
        setAgentsLoaded(true);
      } else {
        getPageOfUsers(false, 25, 0).then(() => {
          setAgentsLoaded(true);
        });
      }

      const canGetExternal =
        hasPermission(PERMS.ContactsAudit) || hasPermission(PERMS.ContactsOwn);
      if (canGetExternal) {
        getPageOfUsers(true, 25, 0);
      }
    }
  }, [
    agentsLoaded,
    userLoaded,
    permissionsLoaded,
    hasBasic,
    getPageOfUsers,
    hasPermission,
  ]);

  // Replaces old user with new one
  const propagateMerge: UserAPIType["propagateMerge"] = React.useCallback(
    async (from, to) => {
      try {
        const newUser = await getUser(to, DEFAULT_EMBEDS);
        const toUser = formatUser(newUser);
        dispatch(merge({ from, toUser }));
      } catch (err) {
        console.error("Error handling merge event", err);
        dispatch(AddErrorAlert({ err }));
      }
    },
    [dispatch],
  );

  // User events from socket should come here
  const handleEvent: UserAPIType["handleEvent"] = React.useCallback((msg) => {
    setEventQueue((old) => [...old, msg]);
  }, []);

  const processUpdateEvent = React.useCallback(
    async (msg) => {
      const { content } = msg;

      try {
        const user = await getUser(content, DEFAULT_EMBEDS);
        dispatch(updateUserAction(formatUser(user)));

        dispatch(loadUserActivities(user.id, true));
      } catch (err) {
        console.error("Error handling update event", err);
        dispatch(AddErrorAlert({ err }));
      }
    },
    [dispatch],
  );

  // Handles socket events from the queue
  const processEvent = React.useCallback(
    async (msg) => {
      const { action_type: type } = msg;
      if (type === "update") {
        await processUpdateEvent(msg);
      } else {
        console.warn({ UNHANDLED_USER: msg });
      }
    },
    [processUpdateEvent],
  );

  // Processes and clears event queue
  React.useEffect(() => {
    if (!eventQueue.length) {
      return;
    }

    eventQueue.forEach(processEvent);
    setEventQueue([]);
  }, [eventQueue, processEvent]);

  React.useEffect(() => {
    if (user) {
      const formatted = formatUser(user);
      dispatch(updateUserAction(formatted));
    }
  }, [user, dispatch]);

  return (
    <UserAPIContext.Provider
      value={{
        getUsers,
        uploadUser,
        deleteUsers,
        updateUser,
        updateUsers,
        loadUser,
        findChannels,
        propagateMerge,
        updateUserMeta,
        getPageOfUsers,
        resetPagination,
        gotoPage,
        allUsers: users,
        loading: userLoading,
        loaded: userLoaded,
        pagination,
        error,
        userNotFound,
        setUserNotFound,
        handleEvent,
      }}
    >
      {children}
    </UserAPIContext.Provider>
  );
};

export default UserAPIProvider;

export const useUserAPI = () => React.useContext(UserAPIContext);
